import React, { useCallback } from 'react';
import styles from './CVChart.module.css';

// Services
import { cssvar, groupDataByType, getSeriesByType } from '../../../services/charts';

// Hooks
import { useMemo } from 'react';

// Language
import { useTranslation } from 'react-i18next';

// Other
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';

import { LineChart } from 'echarts/charts';
import {
    GridComponent, TooltipComponent, MarkPointComponent, MarkLineComponent,
    MarkAreaComponent, DataZoomSliderComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import moment from 'moment';

// Interfaces
import { Case } from '../../../interfaces/Patient';
import { DataElement } from '../../../interfaces/PhysiologicalMonitoring';
import { Parameter } from '../../../interfaces/Profile';
interface EchartItemStyle {
    color?: string
    borderColor?: string
    borderType?: string
    borderWidth?: number
}

// Constants
const MULT_FACTOR = 0.5;


const CVChart: React.FC<{
    caso: Case
    parameters: Parameter[]
    elements: DataElement[] | undefined,
}> = ({ caso, parameters, elements }) => {

    const { t } = useTranslation();

    const types = useMemo(() => {
        return parameters.map(p => p.id);
    }, [parameters])

    const data = useMemo(() => groupDataByType(elements, types), [elements, types])

    let endHad: string | undefined | null = null;
    let endSevenDays: moment.Moment | string | undefined | null = null;
    let endThirtyDays: moment.Moment | string | undefined | null = null;

    const addDays = (date: string | undefined | null, days: number) => {
        if (typeof date !== 'string') return null;
        const d = new Date(date);
        const d_string = moment(d).add(days, 'days');
        return moment(d_string, 'YYYY-MM-DD[T]HH:mm:ssZ').format();
    };

    if (caso.out_days !== undefined && caso.out_days !== null) {
        const diffDate = Number(caso.out_days);
        if (diffDate === -1) {
            endHad = null;
            endSevenDays = null;
            endThirtyDays = null;
        }
        else if (diffDate <= 7) {
            endHad = caso.end_date;
            endSevenDays = addDays(endHad, 7);
            endThirtyDays = null;
        }
        else if (diffDate > 7) {
            endHad = caso.end_date;
            endSevenDays = addDays(endHad, 7);
            endThirtyDays = addDays(endHad, diffDate);
        }
    }

    const findNearestDate = (datesToBeChecked: string[], dateToCheckFor: moment.MomentInput | null) => {

        let nearestDate: moment.MomentInput;
        if (dateToCheckFor != null) {
            datesToBeChecked.forEach(date => {
                const diff = moment(date).diff(moment(dateToCheckFor), 'days');
                if (diff > 0) {
                    if (nearestDate) {
                        if (moment(date).diff(moment(nearestDate), 'days') < 0) {
                            nearestDate = date;
                        }
                    } else {
                        nearestDate = date;
                    }
                }
            });
        } else {
            nearestDate = datesToBeChecked[datesToBeChecked.length - 1];
        }
        return nearestDate?.toString();
    };


    const replaceSpace = (toReplace: string | null | undefined) => {
        return (!toReplace) ? '' : toReplace.replace(' ', '\n');
    };

    const createMarkArea = (show: boolean, name: string, xAxis1: string, xAxis2: string, itemStyle: EchartItemStyle) => {
        if (!window.config.CHARTS_WITH_SECTIONS) return;
        return {
            type: 'line',
            markArea: {
                silent: true,
                label: {
                    show,
                    position: 'insideTopLeft',
                    distance: 20,
                    color: cssvar('--secondary-color'),
                    fontFamily: 'MontserratBold',
                },
                data: [[{ name, xAxis: xAxis1 }, { xAxis: xAxis2 }]],
                itemStyle
            }
        };
    };

    const { parametersMin, parametersMax } = useMemo(() => {
        let parametersMin: number | null = null, parametersMax: number | null = null;
        parameters.forEach(p => {
            if (typeof p.min_limit === 'number') {
                if (parametersMin === null || p.min_limit < parametersMin)
                    parametersMin = p.min_limit;
            }
            if (typeof p.max_limit === 'number') {
                if (parametersMax === null || p.max_limit < parametersMax)
                    parametersMax = p.max_limit;
            }
        })
        return { parametersMin, parametersMax }
    }, [parameters]);

    const getChartLimitMin = useCallback(({ min: defaultMin, max: defaultMax }: { min: number, max: number }) => {
        const range = defaultMax - defaultMin;
        const min = defaultMin - range * MULT_FACTOR;
        return Math.round(parametersMin === null ? min : Math.min(parametersMin - range * MULT_FACTOR, min));
    }, [parametersMin]);

    const getChartLimitMax = useCallback(({ min: defaultMin, max: defaultMax }: { min: number, max: number }) => {
        const range = defaultMax - defaultMin;
        const max = defaultMax + range * MULT_FACTOR;
        return Math.round(parametersMax === null ? max : Math.max(parametersMax + range * MULT_FACTOR, max));
    }, [parametersMax]);

    const chartOption = useMemo(() => {

        if (!data || data.length === 0 || data[0].length === 0) return {};

        const timestamps = data[0].map(timestamp => timestamp.date.replace(' ', '\n'));

        return {
            xAxis: {
                type: 'category',
                data: timestamps,
            },
            yAxis: {
                type: 'value',
                // splitNumber: 5,
                min: getChartLimitMin,
                max: getChartLimitMax,
            },
            dataZoom: [
                {
                    type: 'slider',
                    id: caso.id,
                    zoomLock: false,
                    realtime: true,
                    start: 0,
                    end: 100,
                    dataBackground: {
                        areaStyle: { color: 'rgba(221, 221, 221, 1)' },
                        lineStyle: { color: 'rgba(0,0,0, 0.1)' }
                    },
                    selectedDataBackground: {
                        areaStyle: { color: 'rgba(0,0,0, 0.1)' },
                        lineStyle: { color: 'rgba(0,0,0, 0.2)' }
                    },
                    fillerColor: cssvar('--secondary-color-lighter'),
                    borderColor: 'rgba(0,0,0,0.08)',
                    handleStyle: {
                        color: cssvar('--secondary-color-light'),
                        borderColor: 'rgba(0,0, 0, 0.2)'
                    },
                    moveHandleSize: 0,
                    height: 30,
                    emphasis: {
                        handleStyle: { borderColor: 'rgba(0,0,0,0.5)' }
                    }
                }
            ],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                    shadowStyle: { color: 'rgba(0, 0, 0, 0.03)' }
                },
                className: styles.tooltipClass
            },
            series: [
                ...parameters.map(param => {
                    const seriesObj = getSeriesByType(param, data);
                    seriesObj.name = t(seriesObj.name);
                    return seriesObj;
                }),
                createMarkArea( endHad === null,
                    t('HAD'),
                    timestamps[0],
                    replaceSpace(findNearestDate(timestamps, endHad)),
                    { color: cssvar('--discharge-status-had') }
                ),
                createMarkArea(endSevenDays != null,
                    t('minus_equal_7_days_discharge'),
                    replaceSpace(findNearestDate(timestamps, endHad)),
                    replaceSpace(findNearestDate(timestamps, endSevenDays)),
                    { color: cssvar('--discharge-status-seven-days') }
                ),
                createMarkArea(endThirtyDays != null,
                    t('less_30_days_discharge'),
                    replaceSpace(findNearestDate(timestamps, endSevenDays)),
                    timestamps[timestamps.length - 1],
                    {
                        color: cssvar('--discharge-status-thirty-days'),
                        borderColor: cssvar('--discharge-status-had'),
                        borderType: 'solid',
                        borderWidth: 1
                    }
                ),
            ],
            textStyle: { fontFamily: 'MontserratRegular' },
            grid: {
                show: true,
                borderWidth: 0,
                left: 35,
                top: 5,
                right: 30,
                bottom: 100
            }
        };
    }, [caso.id, data, endHad, endSevenDays, endThirtyDays, getChartLimitMax, getChartLimitMin, parameters, t])

    echarts.use([LineChart, GridComponent, DataZoomSliderComponent,
        TooltipComponent, MarkAreaComponent, CanvasRenderer,
        MarkLineComponent, MarkPointComponent,
    ]);

    return (
        <>{data && data.length > 0 && data[0].length > 0 ?
            <ReactEChartsCore
                echarts={echarts}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
                option={chartOption} />
            :
            <div className={styles.noDataMsg}>
                <p>{t('no_data')}</p>
            </div>
        }</>
    );
};

export default CVChart;